import { useCallback, useEffect, useRef } from 'react';

import { CUSTOMER_DATA_TABS, LEAD_DETAILS_FORMS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useForm } from 'react-hook-form';

import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useTabErrorNotification } from '~/features/leadDetails/hooks/useTabErrorNotification.ts';
import type { SchemaIncomeForm } from '~/features/leadDetails/schemas/income.schema.ts';
import { resolverIncomeForm } from '~/features/leadDetails/schemas/income.schema.ts';
import { generateIncomeCommands } from '~/features/leadDetails/utils/generateIncomeCommands.ts';
import { submitCommands } from '~/features/leadDetails/utils/submitCommands.ts';
import { toIncomeFormValues } from '~/features/leadDetails/utils/toIncomeFormValues.ts';
import { useOnMountAndChange } from '~/hooks/useOnMountAndChange.tsx';

const formId = LEAD_DETAILS_FORMS.INCOME;

export const useIncomeForm = (lead: SchemaLoanRequestResolver_Get) => {
    const { registerDirtyState, registerReset, registerSubmit } = useLeadDetailsFormContext();
    const methods = useForm<SchemaIncomeForm>({
        defaultValues: toIncomeFormValues(lead.haushalt.einnahmen),
        mode: 'onChange',
        resolver: resolverIncomeForm,
    });
    const hadAntragsteller2 = useRef(!!lead.antragsteller2);

    useEffect(() => {
        if (!hadAntragsteller2.current && lead.antragsteller2) {
            hadAntragsteller2.current = true;
            methods.reset(toIncomeFormValues(lead.haushalt.einnahmen));
        }
    }, [lead, lead.antragsteller2, methods]);

    useTabErrorNotification(
        CUSTOMER_DATA_TABS.INCOME_AND_EXPENSES,
        methods.formState.errors,
        LEAD_DETAILS_FORMS.INCOME,
    );

    useEffect(() => {
        registerDirtyState(formId, {
            dirty: methods.formState.isDirty,
            dirtyFields: methods.formState.dirtyFields,
        });
    }, [registerDirtyState, methods.formState, methods.formState.isDirty]);

    registerReset(formId, methods.reset);

    useOnMountAndChange(() => {
        registerSubmit(
            (setSubmitStatus) =>
                methods.handleSubmit(
                    async (data) => {
                        const commands = generateIncomeCommands(lead.haushalt.einnahmen, data);
                        await submitCommands(lead.id, setSubmitStatus, commands, methods.reset);
                    },
                    (error) => setSubmitStatus({ changes: true, error, saved: false }),
                ),
            formId,
            methods.trigger,
        );
    }, [lead.haushalt.einnahmen, lead.id, methods, registerSubmit]);

    const onDelete = useCallback(
        (type: keyof SchemaIncomeForm, id: string) => {
            const incomes = methods.getValues(type);
            methods.setValue(
                type,
                (incomes ?? []).filter((income) => income.id !== id),
                { shouldDirty: true },
            );
        },
        [methods],
    );

    return { methods, onDelete };
};

export type IncomeFormState = ReturnType<typeof useIncomeForm>;
